// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import submitEmailsForm from "@rc/App/Handlers/EmailForms/SubmitEmailForms";
import Select from "@rc/MobiusOverrides/Select/Select";
import TextField from "@rc/MobiusOverrides/TextField";
import { xValidateOnSubmit } from "@rc/XDL/Utilities";
import parse from "html-react-parser";
import React, { RefObject, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";

const enum tabFields {
    display = "display",
    value = "value",
}

interface TabsProps {
    fields: {
        [tabFields.display]: string;
        [tabFields.value]: string;
    };
}

const enum fields {
    emailFormType = "emailFormType",
    industryOptions = "industryOptions",
    eventOptions = "eventOptions",
    legalTermsLabel = "legalTermsLabel",
    optInLabel = "optInLabel",
    submitButtonLabel = "submitButtonLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailFormType]: string;
        [fields.industryOptions]: TabsProps[];
        [fields.eventOptions]: TabsProps[];
        [fields.legalTermsLabel]: string;
        [fields.optInLabel]: string;
        [fields.submitButtonLabel]: string;
    };
}

const mapDispatchToProps = {
    submitEmailsForm,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasToasterContext;

export interface EmailFormsStyles {
    container?: GridContainerProps;
    firstNameGridItem?: GridItemProps;
    lastNameGridItem?: GridItemProps;
    emailAddressGridItem?: GridItemProps;
    phoneNumberGridItem?: GridItemProps;
    companyGridItem?: GridItemProps;
    industryGridItem?: GridItemProps;
    eventGridItem?: GridItemProps;
    termsGridItem?: GridItemProps;
    optInGridItem?: GridItemProps;
    contentGridItem?: GridItemProps;
    submitButtonGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
}

export const emailFormsStyles: EmailFormsStyles = {};

const styles = emailFormsStyles;

const formId = "emailForm";

const EmailForms = ({ id, fields, toaster, submitEmailsForm, validateReCaptcha }: Props) => {
    const { emailFormType, eventOptions, industryOptions, optInLabel, submitButtonLabel, legalTermsLabel } = fields;
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [company, setCompany] = useState("");
    const [industry, setIndustry] = useState("");
    const [emailEvent, setEmailEvent] = useState("");
    const [legalTerms, setLegalTerms] = useState(false);
    const [optIn, setOptIn] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [phoneNumberErrorMessage, setPhoneNumberErrorMessage] = useState("");
    const [eventErrorMessage, setEventErrorMessage] = useState("");
    const [isPhoneBlurred, setIsPhoneBlurred] = useState(false);

    const phoneRegex = /^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/;

    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const clearFormFields = () => {
        setFirstName("");
        setLastName("");
        setEmailAddress("");
        setPhoneNumber("");
        setCompany("");
        setIndustry("");
        setEmailEvent("");
        setLegalTerms(false);
        setOptIn(false);
    };

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleEventBlur();
        setIsSubmitted(true);
        const errors = xValidateOnSubmit(formId);
        const isReCaptchaValid = await validateReCaptcha({ location: "EmailForms", recaptchaRef });

        if (errors.length > 0 || !legalTerms || !optIn || !emailEvent || !isReCaptchaValid) {
            return;
        }

        setIsLoading(true);

        submitEmailsForm({
            firstName,
            lastName,
            emailAddress,
            phoneNumber,
            company,
            industry,
            event: emailEvent,
            legalTerms,
            optIn,
            emailFormType,
            onSuccess: () => {
                clearFormFields();
                toaster.addToast({
                    body: siteMessage("Email_Forms_Success_Message"),
                    messageType: "success",
                });
            },
            onError: (errorMessage: any) => {
                let error = errorMessage;

                if (Object.keys(errorMessage).length) {
                    error = errorMessage?.message;
                }

                toaster.addToast({ body: error || siteMessage("Email_Forms_Failure_Message"), messageType: "danger" });
            },
            onComplete: () => {
                setIsLoading(false);
                setIsSubmitted(false);
            },
        });
    };

    const validatePhoneNumber = (phone: string) => {
        const errorMessage =
            !phone || phoneRegex.test(phone) ? "" : siteMessage("ContactUsForm_PhoneNumberIsInvalidErrorMessage");
        setPhoneNumberErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    const phoneNumberBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validatePhoneNumber(phoneNumber);
        setIsPhoneBlurred(true);
    };

    const handleEventBlur = () => {
        !emailEvent && setEventErrorMessage(translate("Event is required"));
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <form id="emailForm" onSubmit={handleFormSubmit} noValidate>
                <GridContainer {...styles.container}>
                    <GridItem {...styles.industryGridItem}>
                        <Select
                            label={translate("Industry")}
                            value={industry}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                                setIndustry(event.currentTarget.value)
                            }
                            data-test-selector="industry"
                            {...{ formId }}
                        >
                            <option value="">{translate("Select an option")}</option>
                            {industryOptions?.map((option: any) => (
                                <option key={option.fields?.value} value={option.fields?.value}>
                                    {option.fields?.display}
                                </option>
                            ))}
                        </Select>
                    </GridItem>

                    <GridItem {...styles.eventGridItem}>
                        <Select
                            label={translate("Event*")}
                            value={emailEvent}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                setEmailEvent(event.currentTarget.value);
                                if (event.currentTarget.value) {
                                    setEventErrorMessage("");
                                }
                            }}
                            onBlur={() => {
                                handleEventBlur();
                            }}
                            error={eventErrorMessage}
                            required
                            data-test-selector="event"
                            {...{ formId }}
                        >
                            <option value="">{translate("Select an option")}</option>
                            {eventOptions?.map((option: any) => (
                                <option key={option.fields?.value} value={option.fields?.value}>
                                    {option.fields?.display}
                                </option>
                            ))}
                        </Select>
                    </GridItem>

                    <GridItem {...styles.firstNameGridItem}>
                        <TextField
                            label={translate("First Name*")}
                            value={firstName}
                            onChange={(event: any) => setFirstName(event.target.value)}
                            required
                            data-test-selector="firstName"
                            maxLength={75}
                            type="fullName"
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.lastNameGridItem}>
                        <TextField
                            label={translate("Last Name*")}
                            value={lastName}
                            onChange={(event: any) => setLastName(event.target.value)}
                            required
                            data-test-selector="lastName"
                            maxLength={75}
                            type="fullName"
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.emailAddressGridItem}>
                        <TextField
                            label={translate("Email Address*")}
                            value={emailAddress}
                            onChange={(event: any) => setEmailAddress(event.target.value)}
                            data-test-selector="emailAddress"
                            type="email"
                            required
                            maxLength={75}
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.phoneNumberGridItem}>
                        <TextField
                            label={translate("Phone Number")}
                            value={phoneNumber}
                            onChange={(event: any) => {
                                setPhoneNumber(event.target.value);
                                validatePhoneNumber(event.target.value);
                            }}
                            onBlur={phoneNumberBlurHandler}
                            error={isPhoneBlurred && phoneNumberErrorMessage}
                            data-test-selector="Phone Number"
                            type="phone"
                            maxLength={15}
                        />
                    </GridItem>
                    <GridItem {...styles.companyGridItem}>
                        <TextField
                            label={translate("Company*")}
                            value={company}
                            onChange={(event: any) => setCompany(event.target.value)}
                            data-test-selector="company"
                            maxLength={75}
                            type="fullName"
                            required
                            {...{ formId }}
                        />
                    </GridItem>

                    <GridItem {...styles.termsGridItem}>
                        <Checkbox
                            checked={legalTerms}
                            onChange={(event: any, value) => setLegalTerms(value)}
                            data-test-selector="legal_terms_checkbox"
                            error={!legalTerms && isSubmitted}
                            {...{ formId }}
                        >
                            {parse(legalTermsLabel, parserOptions)}
                        </Checkbox>
                    </GridItem>

                    <GridItem {...styles.optInGridItem}>
                        <Checkbox
                            checked={optIn}
                            onChange={(event: any, value) => setOptIn(value)}
                            data-test-selector="opt_in_checkbox"
                            error={!optIn && isSubmitted}
                            {...{ formId }}
                        >
                            {parse(optInLabel, parserOptions)}
                        </Checkbox>
                    </GridItem>

                    <GridItem width={12} {...styles.contentGridItem}>
                        <Zone contentId={id} zoneName="Content" />
                    </GridItem>

                    <ReCaptcha recaptchaRef={recaptchaRef} location="EmailForms" />

                    <GridItem {...styles.submitButtonGridItem}>
                        <Button type="submit" {...styles.submitButton} data-test-selector="submit_email_form">
                            {submitButtonLabel}
                        </Button>
                    </GridItem>
                </GridContainer>
            </form>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(withToaster(EmailForms)),
    definition: {
        displayName: "Email Forms",
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.emailFormType,
                editorTemplate: "DropDownField",
                defaultValue: "SaveVetEmailForm",
                fieldType: "General",
                options: [
                    { displayName: "Vet Email Form", value: "SaveVetEmailForm" },
                    { displayName: "Pro Email Form", value: "SaveProEmailForm" },
                ],
                sortOrder: 1,
            },
            {
                name: fields.industryOptions,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.display;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: tabFields.display,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: tabFields.value,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
                sortOrder: 2,
            },
            {
                name: fields.eventOptions,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.display;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: tabFields.display,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: tabFields.value,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
                sortOrder: 3,
            },
            {
                name: fields.legalTermsLabel,
                displayName: "Terms Checkbox Label",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
                sortOrder: 4,
            },
            {
                name: fields.optInLabel,
                displayName: "Opt In Checkbox Label",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
                sortOrder: 5,
            },
            {
                name: fields.submitButtonLabel,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Send",
                sortOrder: 6,
            },
        ],
    },
};

export default widgetModule;
